// https://github.com/radix-ui/primitives/blob/main/packages/react/toggle-group/src/ToggleGroup.tsx

import React, { createContext, useContext, useCallback } from 'react';
import styled from 'styled-components';
import { Badge } from './Badge';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { MdOutlineCircle } from 'react-icons/md';
import { useControllableState } from './useControllableState';
import { Fonts } from '../utils';

const ToggleGroupContext = createContext();

export const useToggleGroupContext = () => useContext(ToggleGroupContext);

export const ToggleGroup = (props) => {
  const { value: valueProp = [], defaultProp = [], onValueChange = (values) => { }, single, children } = props;

  const [values = [], setValue] = useControllableState({
    prop: valueProp,
    defaultProp,
    onChange: onValueChange,
  });

  const handleButtonActivate = useCallback(
    (itemValue) => {
      if (single) {
        setValue(() => {
          const newValue = [itemValue];
          onValueChange(newValue);
          return newValue;
        });
      } else {
        setValue((prevValue = []) => {
          const newValue = [...prevValue, itemValue];
          onValueChange(newValue);
          return newValue;
        });
      }
    },
    [setValue, single]
  );

  const handleButtonDeactivate = useCallback(
    (itemValue) => {
      if (single) {
        setValue(() => {
          const newValue = [];
          onValueChange(newValue);
          return newValue;
        });
      } else {
        setValue((prevValue = []) => {
          const newValue = [...prevValue.filter((value) => value !== itemValue)];
          onValueChange(newValue);
          return newValue;
        });
      }
    },
    [setValue, single]
  );

  return (
    <ToggleGroupContext.Provider
      value={{
        values,
        onItemActivate: handleButtonActivate,
        onItemDeactivate: handleButtonDeactivate
      }}>
      {children}
    </ToggleGroupContext.Provider>
  );
};

// TODO: Make this component more generic so it isn;t only used for filter options

export const ToggleGroupItem = ({ value, children, ...rest }) => {
  const toggleGroupContext = useToggleGroupContext();
  const checked = toggleGroupContext.values.includes(value);

  return (
    <StyledBadge
      variant={checked ? 'selected' : 'unselected'}
      onClick={() => {
        if (checked) {
          toggleGroupContext.onItemDeactivate(value);
        } else {
          toggleGroupContext.onItemActivate(value);
        }
      }}
      {...rest}>
      {checked ? <IoCheckmarkCircle size={18} /> : <MdOutlineCircle size={18} />}
      {children}
    </StyledBadge>
  );
};

const StyledBadge = styled(Badge)`
  cursor: pointer;
  gap: 8px;
  padding: 0.125rem 0.5rem;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;

  color: #133155;
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 600;
  line-height:  1.5rem;
  letter-spacing: 0px;
`;
